// Ported from dev-scrips-pm-react src/billing/components/invoice-pending-orders-dialogs.tsx (PROD-3312 appointment Billing
// tab, step 6): the Add-orders confirm (AlertDialog) and the Dismiss popover (reason + note). PM primitives come from the
// PROD-3310 billing bridge. Differences from pm-react, none of them behavioural:
//   - `text-[var(--color-brand-depth)]` / `accent-[var(--color-brand-primary)]` are the named `text-brand-depth` /
//     `accent-brand-primary` (the PA forbids arbitrary classes; same tokens).
//   - the dismiss form is its own component, mounted only while the popover is open, so every open starts with no reason
//     and no note. pm-react reset both in `onOpenChange(false)`; same observable behaviour, no reset code.
import { useState, type ReactElement } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  BridgeButton as Button,
  BridgeTooltipTrigger as TooltipTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
  Tooltip,
  TooltipContent,
} from '@/billing/bridge';
import { addedByLabel } from '@/billing/lib/line-chip';
import { formatMoney } from '@/shared/lib/money';
import type { InvoiceOrderDismissReason } from '@/shared/types/billing';
import {
  DISMISS_REASONS,
  DISMISS_REASON_LABEL,
  DISMISS_TOOLTIP,
  joinNames,
} from '@/billing/lib/pending-orders';

/** Primary confirm button colour used by the invoice page's own dialogs. */
const DEPTH_BUTTON_CLASS = 'bg-brand-depth hover:bg-brand-primary text-white';

export interface AddOrdersConfirmTarget {
  /** Order names, one per order (a vaccine's two lines are one order). */
  names: string[];
  /** Sum of the indicative fees being added. */
  amount: number;
}

interface AddOrdersConfirmDialogProps {
  target: AddOrdersConfirmTarget | null;
  currency: string;
  /** Patient Payable before the add; with `selfPay` shows the "a -> b" line. */
  patientPayable?: number;
  selfPay: boolean;
  userName: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * "Add ECG, 12-lead to the invoice now?" (canvas board 2 / 2b). The add moves
 * money into Patient Payable, so it is confirmed; dismiss is not (it moves none).
 */
export function AddOrdersConfirmDialog({
  target,
  currency,
  patientPayable,
  selfPay,
  userName,
  pending,
  onCancel,
  onConfirm,
}: AddOrdersConfirmDialogProps) {
  const count = target?.names.length ?? 0;
  const single = count === 1;
  const amount = target?.amount ?? 0;
  const money = (n: number) => `${currency} ${formatMoney(n)}`;
  const title = single
    ? `Add ${target?.names[0]} to the invoice now?`
    : `Add ${count} orders to the invoice now?`;
  const description = single
    ? 'The order stays as it is in Assessment & Plan. Only the invoice changes.'
    : `${joinNames(target?.names ?? [])}. The orders stay as they are in Assessment & Plan.`;

  return (
    <AlertDialog open={target !== null} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="flex flex-col gap-2 px-1 text-sm text-text-secondary">
          <li className="flex gap-2">
            <span className="text-text-muted" aria-hidden>•</span>
            {selfPay && patientPayable !== undefined ? (
              <span>
                <strong className="font-semibold text-text-primary">{money(amount)}</strong> moves
                into Patient Payable ({money(patientPayable)} → {money(patientPayable + amount)}).
              </span>
            ) : (
              <span>
                <strong className="font-semibold text-text-primary">{money(amount)}</strong> is
                added to the invoice. Coverage decides how much of it the patient pays.
              </span>
            )}
          </li>
          <li className="flex gap-2">
            <span className="text-text-muted" aria-hidden>•</span>
            <span>
              {single ? 'The line is' : 'Each line is'} tagged{' '}
              <em className="not-italic font-medium text-brand-depth">{addedByLabel(userName)}</em> with the
              time.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-text-muted" aria-hidden>•</span>
            <span>
              {single
                ? 'If the order is cancelled later: before issue you can revert the line to pending or drop it; after issue it needs a credit or refund.'
                : 'If an order is cancelled later: before issue you can revert or drop its line; after issue it needs a credit or refund.'}
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-text-muted" aria-hidden>•</span>
            <span>
              {single
                ? 'Dropping the line later never cancels the order.'
                : 'Dropping a line later never cancels its order.'}
            </span>
          </li>
        </ul>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={DEPTH_BUTTON_CLASS}
            disabled={pending}
            // Stays open until the request settles; the parent closes it. The bridge action never auto-closes.
            onClick={onConfirm}
          >
            {single ? 'Add to invoice' : `Add ${count} to invoice`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export interface DismissChoice {
  reason?: InvoiceOrderDismissReason;
  note?: string;
}

interface DismissOrdersPopoverProps {
  /** "Dismiss HbA1c from this invoice?" / "Dismiss 2 orders from this invoice?" */
  title: string;
  /** The trigger element (the row's or the bulk bar's Dismiss button). */
  trigger: ReactElement;
  pending: boolean;
  /** Resolve true to close the popover (the request succeeded). */
  onConfirm: (choice: DismissChoice) => Promise<boolean>;
}

const NOTE_MAX = 500;

/**
 * Dismiss with an optional reason (canvas board 2c). "Other" reveals a short
 * note. Dismissing never cancels the clinical order, which the copy says.
 */
export function DismissOrdersPopover({ title, trigger, pending, onConfirm }: DismissOrdersPopoverProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* The styled tooltip rides on the same button as the popover trigger:
          base-ui composes the two triggers through `render`. */}
      <Tooltip>
        <TooltipTrigger render={<PopoverTrigger render={trigger} />} />
        <TooltipContent className="max-w-60">{DISMISS_TOOLTIP}</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="w-72" aria-label={title}>
        {open && <DismissForm title={title} pending={pending} onConfirm={onConfirm} onClose={() => setOpen(false)} />}
      </PopoverContent>
    </Popover>
  );
}

interface DismissFormProps extends Pick<DismissOrdersPopoverProps, 'title' | 'pending' | 'onConfirm'> {
  onClose: () => void;
}

function DismissForm({ title, pending, onConfirm, onClose }: DismissFormProps) {
  const [reason, setReason] = useState<InvoiceOrderDismissReason | undefined>(undefined);
  const [note, setNote] = useState('');

  const submit = async () => {
    const trimmed = note.trim();
    const ok = await onConfirm({
      reason,
      note: reason === 'Other' && trimmed ? trimmed : undefined,
    });
    if (ok) onClose();
  };

  return (
    <>
      <div className="flex flex-col gap-1">
        <div className="text-sm font-medium text-text-primary">{title}</div>
        <div className="text-xs text-text-muted">{DISMISS_TOOLTIP}</div>
      </div>
      <fieldset className="flex flex-col">
        <legend className="mb-1 text-xs font-medium text-text-muted">Reason (optional)</legend>
        {DISMISS_REASONS.map((r) => (
          <label key={r} className="flex h-8 cursor-pointer items-center gap-2 text-sm text-text-primary">
            <input
              type="radio"
              name="dismiss-reason"
              value={r}
              checked={reason === r}
              onChange={() => setReason(r)}
              className="size-4 accent-brand-primary"
            />
            {DISMISS_REASON_LABEL[r]}
          </label>
        ))}
      </fieldset>
      {reason === 'Other' && (
        <div className="flex flex-col gap-1">
          <Textarea
            aria-label="Note"
            aria-describedby="dismiss-note-count"
            placeholder="Add a short note (optional)"
            maxLength={NOTE_MAX}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="min-h-16 text-sm"
          />
          <span id="dismiss-note-count" className="self-end text-xs text-text-muted tabular-nums">
            {note.length}/{NOTE_MAX}
          </span>
        </div>
      )}
      <div className="flex justify-end gap-2 border-t border-border-default pt-2.5">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button size="sm" className={DEPTH_BUTTON_CLASS} onClick={submit} disabled={pending}>
          Dismiss
        </Button>
      </div>
    </>
  );
}
